import { Fragment, type ReactElement } from 'react'
import { observer } from 'mobx-react-lite'
import { tokens } from '@fluentui/tokens'
import { makeStyles, mergeClasses } from '@griffel/react'
import { Text } from '@fluentui/react-text'
import { GenericList, useGenericListSettings } from '@knowmax/genericlist-core'
import { Loader, ErrorMessage } from '../../components/core'
import { LANGUAGE_LABELNORESULTS } from '../../types/language'
import { ListPanel, PagerWrapper } from '.'

export type TOnRenderListItem<T> = (item: T, index: number) => ReactElement

export type TListLayout = 'list' | 'grid' | 'compact' | 'card'

export interface IListProps<T = unknown> {
  list: GenericList<T>
  onRenderListItem: TOnRenderListItem<T>
  showPager?: boolean
  layout?: TListLayout
  className?: string
}

const useClasses = makeStyles({
  listroot: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingVerticalM
  },
  compact: {
    rowGap: tokens.spacingVerticalXS
  },
  card: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingVerticalM
  }
})

const layoutClassMap = (classes: ReturnType<typeof useClasses>): Partial<Record<TListLayout, string>> => ({
  grid: classes.grid,
  compact: classes.compact,
  card: classes.card,
})

/** Generic component to render list for items in provided @see GenericList. */
// somehow I need to use <T,> since <T> is not working
export const List = observer(<T,>({ list, onRenderListItem, showPager = true, layout = 'list', className }: IListProps<T>) => {
  const classes = useClasses()
  const settings = useGenericListSettings()

  return (
    <>
      {list.error &&
        <ErrorMessage error={list.error} />
      }
      {(!list.loaded || list.list.length === 0) && list.loading &&
        <Loader />
      }
      {list.loaded && !list.loading && list.list.length === 0 &&
        <Text>{settings.onTranslate(LANGUAGE_LABELNORESULTS)}</Text>
      }
      <div className={mergeClasses(classes.listroot, className)}>
        {list.loaded &&
          <ListPanel loading={list.loading} className={layoutClassMap(classes)[layout]}>
            {list.list.map((item, index) => <Fragment key={index}>{onRenderListItem(item, index)}</Fragment>)}
          </ListPanel>
        }
        {showPager &&
          <PagerWrapper list={list} />
        }
      </div>
    </>
  )
})